<script lang="tsx">
  import { isRef, ref, unref, useId, useModel, useTemplateRef, type MaybeRef, type PropType, type Ref } from "vue";
  import { Button, FileUpload, Message, Skeleton, Tooltip, type FileUploadState } from "primevue";

  import { useSortable } from "@vueuse/integrations/useSortable";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faChevronLeft, faChevronRight, faGripVertical, faTrash } from "@fortawesome/free-solid-svg-icons";

  import { graphql as gql } from "@/gql";

  interface AssetData {
    id: number;
    url: string;
    height: number;
    width: number;
    title: string;
    alt: string;
  }

  export default {
    directives: {
      tooltip: Tooltip
    },
    components: { Button, Message, Skeleton, FileUpload, Icon },
    props: {
      initialAssets: Array as PropType<number[]>,
      assets: {
        type: Array as PropType<MaybeRef<number[]>>,
        default() { return ref([] as number[]); },
      },
      volume: {
        type: String,
        required: true
      },
      accept: String,
      disabled: Boolean,
      maxFileSize: Number,
      assetLimit: Number,
    },
    emits: ["update:assets"],
    async setup(props) {
      const vid = useId();

      const assets = useModel(props, "assets");
      if (unref(assets.value).length == 0 && (props.initialAssets?.length ?? 0) > 0) {
        unref(assets.value).push(...props.initialAssets!);
      }

      useSortable(
        useTemplateRef<HTMLDivElement>("list"),
        assets,
        {
          handle: `.${vid}-grip`,
          animation: 200,
          draggable: `.${vid}-drag`,
        }
      );

      return {
        vid,
        assetModel: assets,
      };
    },
    data() {return {
      faTrash, faGripVertical, faChevronLeft, faChevronRight, unref,

      uploadRef: undefined as FileUploadState | undefined,
      assetCache: {} as Record<number, AssetData>,
      pendingUploads: [] as File[],
    }},
    computed: {
      single() { return this.assetLimit == 1; }
    },
    watch: {
      assetModel: {
        handler(ids: number[]) { this.lookup(ids); },
        immediate: true,
        deep: true,
      }
    },
    methods: {
      async lookup(ids: number[]) {
        const fetchIds = [];
        const result: Record<number, AssetData> = {};
        for (const id of ids) {
          if (this.assetCache[id] != undefined) {
            result[id] = this.assetCache[id];
          } else {
            fetchIds.push(id);
          }
        }

        const { data } = await this.$gql.query(
          gql(`
            query FetchUserAssets($ids: [QueryArgument], $volume: String) {
              assets(id: $ids, volume: [$volume]) @transform(transform: "thumb") {
                id
                title
                alt
                url
                height
                width
              }
            }
          `),
          {
            ids: fetchIds,
            volume: this.volume,
          }
        );

        for (const asset of data?.assets ?? []) {
          if (asset?.id != null) {
            const id = parseInt(asset.id);
            result[id] = this.assetCache[id] = {
              id: id,
              url: asset.url!,
              height: asset.height!,
              width: asset.width!,
              title: asset.title!,
              alt: asset.alt!,
            };
          }
        }

        return result;
      },

      async upload(file: File) {
        const reader = new FileReader();
        const url = await new Promise<string>(resolve => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        const filename = file.name.includes(".") ? file.name : undefined;

        this.pendingUploads.push(file);

        const { data, error } = await this.$gql.mutation(
          gql(`
            mutation UploadUserAsset($file: FileInput, $title: String, $userId: ID) {
              save_user_Asset(
                _file: $file
                title: $title
                uploaderId: $userId
              ) {
                id
                url @transform(transform: "thumb")
                width @transform(transform: "thumb")
                height @transform(transform: "thumb")
                title
                alt
              }
            }
          `),
          {
            file: {
              fileData: url,
              filename: filename
            },
            title: filename,
            uid: this.$session.info.value?.id,
          }
        );

        this.pendingUploads.splice(this.pendingUploads.indexOf(file), 1);
        const asset = data?.save_user_Asset;
        if (error == null && asset?.id != null) {
          const id = parseInt(asset.id);
          this.assetCache[id] = {
            id: id,
            url: asset.url!,
            height: asset.height!,
            width: asset.width!,
            title: asset.title!,
            alt: asset.alt!,
          };
          unref(this.assetModel).push(id);
          return this.assetCache[id];
        }
      },

      remove(id: number) {
        if (isRef(this.assetModel)) {
          this.assetModel.value = (this.assetModel as Ref<number[]>).value.filter(a => a != id);
        } else {
          for (let i = 0; i < this.assetModel.length; i++) {
            if (this.assetModel[i] == id) {
              this.assetModel.splice(i, 1);
              i--;
            }
          }
        }
      }
    },
  };
</script>

<template>
  <div :class="{ container: true, card: !single }">
    <div class="list" ref="list">
      <div v-for="assetId, i in assetModel" :class="`card asset ${vid}-drag`">
        <div class="toolbar">
          <div>
            <div v-if="!single" :class="`${vid}-grip grip`" v-tooltip.bottom="'Drag to reorder'" @contextmenu.prevent>
              <Icon class="fa" :icon="faGripVertical"/>
            </div>
            <Button v-if="!single" aria-label="Move backward" v-tooltip.bottom="'Move backward'"
              :disabled="disabled || i === 0 || unref(assetModel).length <= 1"
              variant="text"
              @contextmenu.prevent @click.prevent="{
                [unref(assetModel)[i], unref(assetModel)[i-1]] = [unref(assetModel)[i-1], unref(assetModel)[i]];
              }">
              <template #icon><Icon class="fa" :icon="faChevronLeft"/></template>
            </Button>
            <Button v-if="!single" aria-label="Move forward" v-tooltip.bottom="'Move forward'"
              :disabled="disabled || i >= unref(assetModel).length - 1 || unref(assetModel).length <= 1"
              variant="text"
              @contextmenu.prevent @click.prevent="{
                [unref(assetModel)[i], unref(assetModel)[i+1]] = [unref(assetModel)[i+1], unref(assetModel)[i]];
              }">
              <template #icon><Icon class="fa" :icon="faChevronRight"/></template>
            </Button>
          </div>

          <div>
            <Button aria-label="Delete" v-tooltip.bottom="'Delete'"
              :disabled="disabled"
              raised severity="danger"
              @contextmenu.prevent @click.prevent="remove(assetId)">
              <template #icon><Icon class="fa" :icon="faTrash"/></template>
            </Button>
          </div>
        </div>

        <img v-if="assetCache[assetId] != null" class="image"
          :src="assetCache[assetId]!.url"
          :title="assetCache[assetId]!.title" :alt="assetCache[assetId]!.alt"
          :width="assetCache[assetId]!.width" :height="assetCache[assetId]!.height"/>
        <Skeleton v-else style="height: 150px"/>
      </div>

      <!-- eslint-disable-next-line vue/no-unused-vars -->
      <div v-for="pending in pendingUploads" class="card asset pending">
        <div class="toolbar">
          <div>
            <div v-if="!single"><Icon class="fa" :icon="faGripVertical" style="opacity: 0.6;"/></div>
            <Button v-if="!single" disabled variant="text">
              <template #icon><Icon class="fa" :icon="faChevronLeft"/></template>
            </Button>
            <Button v-if="!single" disabled variant="text">
              <template #icon><Icon class="fa" :icon="faChevronRight"/></template>
            </Button>
          </div>

          <div>
            <Button raised severity="danger" disabled>
              <template #icon><Icon class="fa" :icon="faTrash"/></template>
            </Button>
          </div>
        </div>

        <div class="image">
          <Skeleton style="height: 150px"
            aria-label="Uploading asset" title="Uploading asset"/>
        </div>
      </div>

      <div class="card asset uploader"
        v-show="!(single && unref(assetModel).length + pendingUploads.length >= assetLimit!)">
        <div class="toolbar">
          <div>
            <div><Icon class="fa" :icon="faGripVertical" style="opacity: 0.6;"/></div>
            <Button disabled variant="text">
              <template #icon><Icon class="fa" :icon="faChevronLeft"/></template>
            </Button>
            <Button disabled variant="text">
              <template #icon><Icon class="fa" :icon="faChevronRight"/></template>
            </Button>
          </div>

          <div>
            <Button raised severity="danger" disabled>
              <template #icon><Icon class="fa" :icon="faTrash"/></template>
            </Button>
          </div>
        </div>

        <div class="uploader-content">
          <FileUpload class="button" fluid multiple :ref="(upload) => { uploadRef = upload as any as FileUploadState }"
            :max-file-size="maxFileSize" :accept="accept"
            :disabled="disabled || (assetLimit != null ? unref(assetModel).length + pendingUploads.length >= assetLimit : false)"
            choose-label="Add"
            mode="basic" customUpload auto
            @select="({files}) => {
              if (assetLimit != null) {
                files = files.slice(
                  0,
                  Math.min(assetLimit - (unref(assetModel).length + pendingUploads.length), files.length)
                );
              }

              for (const file of files as File[]) {
                upload(file);
              }
            }"
            :pt="{
              pcMessage: { root: { style: { display: 'none' } } }
            }"
            invalid-file-size-message="File must be smaller than {1}."
            invalid-file-limit-message="Exceeded file limit ({0})."
            invalid-file-type-message="Invalid file type, expected {1}."/>
          <Message
            v-for="message in uploadRef?.messages"
            severity="error" size="small" variant="simple">{{ message }}</Message>
          <Message v-show="assetLimit != null && !single && unref(assetModel).length + pendingUploads.length >= assetLimit" severity="secondary" size="small" variant="simple">
              File limit ({{ assetLimit }}) reached
          </Message>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  @import (reference) "@/style/main.less";

  .container {
    container: asset-list / inline-size;
  }

  .list {
    display: grid;
    grid: auto / 1fr 1fr 1fr 1fr;
    gap: var(--spacing-inline);

    @container (width <= 750px) {
      grid: auto / 1fr 1fr 1fr;
    }

    @container (width <= 550px) {
      grid: auto / 1fr 1fr;
    }

    @container (width <= 350px) {
      grid: auto / 1fr;
    }
  }

  .card {
    background: var(--p-content-background);
    padding: var(--spacing-inline);
    border: 1px solid var(--p-card-border-color);
    border-radius: var(--p-content-border-radius);
    box-shadow: var(--p-card-shadow);
  }

  .asset {
    padding: 0;

    & > .image {
      display: block;
      height: 150px;
      width: 100%;
      object-fit: cover;
      border-end-start-radius: var(--p-content-border-radius);
      border-end-end-radius: var(--p-content-border-radius);
    }

    &.pending > .image {
      border-block-start: 1px solid var(--p-card-border-color);
      height: 149px;
      display: flex;
      justify-content: center;
      align-items: center;

      & > .spinner {
        height: auto;
        max-width: 4em;
        max-height: 100%;
      }
    }

    &.uploader {
      & > .toolbar {
        visibility: hidden;
      }

      & > .uploader-content {
        border-block-start: 1px solid var(--p-card-border-color);
        min-height: 149px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .toolbar {
    padding: var(--spacing-inline);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-inline);

    & > div {
      display: flex;
      align-items: center;
      gap: var(--spacing-inline);
    }

    .grip {
      cursor: grab;
    }
  }
</style>
